import { DateTime, Duration } from "luxon";
import {
  APP_AWS_DATE_FORMAT,
  APP_LONG_DATE_FORMAT,
  APP_SHORT_DATE_FORMAT,
  EMPTY,
  MONTHS,
  WeekdaysMap,
} from "../constants";
import { clamp } from "./extensions";
import { getArrayFromTo } from "./array-utils";
import { isEmpty } from "./string-utils";

export const getLocalISO = (date?: Date): string =>
  date
    ? DateTime.fromJSDate(date)
        .setZone("utc", { keepLocalTime: true })
        .toISO({ suppressMilliseconds: true })!
    : "";

export const getUTCISO = (date?: Date): string =>
  date
    ? DateTime.fromJSDate(date)
        .setZone("utc", { keepLocalTime: false })
        .toISO({ suppressMilliseconds: true })!
    : "";

export const toDateFieldValue = (date: string) =>
  DateTime.fromISO(date).toISODate();

export const areDatesEqual = (date1: Date, date2: Date): boolean =>
  date1?.toLocaleDateString().localeCompare(date2?.toLocaleDateString()) === 0;

export const toAWSDateFormat = (date: DateTime): string =>
  date.toFormat(APP_AWS_DATE_FORMAT, { locale: "en" });

export const toShortDateFormat = (date: DateTime): string =>
  date.toFormat(APP_SHORT_DATE_FORMAT, { locale: "en" });

export const toLongDateFormat = (date: DateTime): string =>
  date.toFormat(APP_LONG_DATE_FORMAT, { locale: "en" });

export const toFormattedDate = (date: Date, format: string): string =>
  DateTime.fromJSDate(date).toFormat(format, { locale: "en" });

export const toFormattedDateUTC  = (date: Date, format: string): string =>
  DateTime.fromJSDate(date, { zone: "utc" }).toFormat(format, { locale: "en" });

export const formatDate = (isoDateString: string | Date): string => {
  if (isoDateString instanceof Date) {
    return toFormattedDate(isoDateString, "ccc. dd-LLL-yyyy");
  }

  return !isEmpty(isoDateString)
    ? toFormattedDate(new Date(isoDateString), "ccc. dd-LLL-yyyy")
    : EMPTY;
};

export const formatDateUTC = (isoDateString: string | Date): string => {
  if (isoDateString instanceof Date) {
    return DateTime.fromJSDate(isoDateString, { zone: "utc" }).toFormat(
      "ccc. dd-LLL-yyyy",
      { locale: "en" }
    );
  }

  return !isEmpty(isoDateString)
    ? DateTime.fromISO(isoDateString, { zone: "utc" }).toFormat(
        "ccc. dd-LLL-yyyy",
        { locale: "en" }
      )
    : EMPTY;
};

export const formatDateUTCwithoutDay = (isoDateString: string | Date): string => {
  if (isoDateString instanceof Date) {
    return DateTime.fromJSDate(isoDateString, { zone: "utc" }).toFormat(
      "dd-LLL-yyyy",
      { locale: "en" }
    );
  }

  return !isEmpty(isoDateString)
    ? DateTime.fromISO(isoDateString, { zone: "utc" }).toFormat(
        "dd-LLL-yyyy",
        { locale: "en" }
      )
    : EMPTY;
};

export const fromUTC1 = (isoDateString: string): Date =>
  !isEmpty(isoDateString)
    ? DateTime.fromISO(isoDateString, { zone: "utc" })
        .minus({ days: 1 })
        .toJSDate()
    : new Date();

export const toUTC1 = (isoDateString: string | Date): Date => {
  if (isoDateString instanceof Date) {
    return getDatePartWithoutTime(
      DateTime.fromJSDate(isoDateString, { zone: "utc" })
        .plus({ days: 1 })
        .toJSDate()
    );
  }

  return !isEmpty(isoDateString)
    ? getDatePartWithoutTime(
        DateTime.fromISO(isoDateString, { zone: "utc" })
          .plus({ days: 1 })
          .toJSDate()
      )
    : getDatePartWithoutTime(new Date());
};

export const formatDateWithTime = (isoDateString: string) =>
  toFormattedDate(new Date(isoDateString), "dd-LLL-yyyy HH:mm a");

export const getHours = (date: string) => new Date(date).getHours();
export const getMinutes = (date: string) => new Date(date).getMinutes();
export const getMinuteFraction = (value: number): number =>
  value < 60 ? value : value / 60;

export const addMinutesToDate = (date: Date, minutes: number): Date =>
  new Date(date.getTime() + minutes * 60 * 1000);

export const addMinutesToDateString = (date: string, minutes: number): Date =>
  addMinutesToDate(new Date(date), minutes);

export const getLocalDateString = (date: Date): string =>
  new Date(date!).toString();

export const getLocalDateStringFromString = (date: string): string =>
  getLocalDateString(new Date(date!));

export const getHoursArrayFromTo = (
  from: number,
  to: number,
  step: number = 60
): AppointmentBookingTime[] => {
  const startDate = new Date(new Date().setUTCHours(from, 0, 0, 0));
  const endDate = new Date(new Date().setUTCHours(to, 0, 0, 0));

  const hoursArray = [];
  let currentDate = startDate;
  while (currentDate <= endDate) {
    hoursArray.push(currentDate);
    //currentDate = new Date(currentDate.getTime() + step * 60 * 1000);
    currentDate = addMinutesToDate(currentDate, step);
  }
  return hoursArray.map((date) => ({
    hour: date.getUTCHours() as string,
    minute: date.getUTCMinutes() as string,
  }));
};

export const dateFromString = (date: string): string => {
  if (!date) return "";
  const parsedDate = DateTime.fromISO(date);

  if (parsedDate.isValid) {
    return toShortDateFormat(parsedDate);
  }
};

export const toFriendlyTime = (timeInMins: number): string => {
  if (timeInMins <= 0) return "Now";

  const duration = Duration.fromObject({ minutes: timeInMins }).shiftTo(
    "days",
    "hours",
    "minutes"
  );

  const parts = [];

  if (duration.days) parts.push(`${duration.days}d`);
  if (duration.hours) parts.push(`${duration.hours}h`);
  if (duration.minutes) parts.push(`${duration.minutes}m`);

  return parts.join(" ");
};

export const convertToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const countBillableDays = (
  dates: Date[],
  excludedDates: Date[]
): number => {
  let count = 0;
  const unavailableSet = new Set(
    excludedDates.map((date) => getLocalISO(date).split("T")[0])
  );
  for (const date of dates) {
    const formattedDate = getLocalISO(date).split("T")[0];
    if (!unavailableSet.has(formattedDate)) {
      count++;
    }
  }
  return count;
};

export const getAllDatesBetween = (
  startDate: Date,
  endDate: Date,
  includeWeekdays: string[] = []
): Date[] => {
  const allDates = [];

  while (startDate < endDate) {
    if (includeWeekdays.includes(WeekdaysMap[startDate.getDay()])) {
      allDates.push(new Date(startDate));
    }
    startDate.setDate(startDate.getDate() + 1);
  }

  return [...allDates];
};

export const getDateAfterMonths = (
  startDate: Date,
  months: number,
  decreaseEndDateBy = 0
) => {
  const endDate = new Date(startDate.setMonth(startDate.getMonth() + months));

  if (decreaseEndDateBy > 0) {
    return new Date(endDate.setDate(endDate.getDate() - decreaseEndDateBy));
  }

  return endDate;
};

export const getMonthNamesAsArray = (total = 12) =>
  MONTHS.slice(0, total).join(",");

export const formatAsMinute = (minute: number): string =>
  minute.toString().padStart(2, "0");

export const formatAsHour = (hour: number): string =>
  hour.toString().padStart(2, "0");

export const getHoursFrom = (min: number, max: number) => {
  const minHour = clamp(min, 0, 23);
  const maxHour = clamp(max, 0, 23);

  return getArrayFromTo(minHour, maxHour).map((hour) => formatAsHour(hour));
};

export const getHourMinuteFromString = (
  time: string
): AppointmentBookingTime => {
  try {
    const [hour, minute] = time.split(":");
    return {
      hour: hour,
      minute: minute,
    };
  } catch (error) {
    return {
      hour: "00",
      minute: "00",
    };
  }
};

export const getMinutesFromSessionTime = (sessionTime: string): number => {
  const bookingTime = getHourMinuteFromString(sessionTime);
  const hour = parseInt(bookingTime.hour);
  const minute = parseInt(bookingTime.minute);

  return hour * 60 + minute;
};

export const getDateWithTime = (
  date: Date,
  time: AppointmentBookingTime
): Date => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    parseInt(time.hour),
    parseInt(time.minute)
  );
};

export const getDateWithTimeFromTimePart = (date: Date, time: string): Date => {
  const timePart = getHourMinuteFromString(time);

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    parseInt(timePart.hour),
    parseInt(timePart.minute)
  );
};

export const getItemsFromArrayComparingDate = <T, K extends keyof T>(
  array: T[],
  key: K,
  value: string
): T[] =>
  array.filter((item) =>
    areDatesEqual(
      new Date(getLocalISO(new Date(item[key] as unknown as string))),
      new Date(getLocalISO(new Date(value)))
    )
  );

export const getDatePart = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const getDatePartWithoutTime = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
};

export const getTimePart = (date: Date): string => {
  return `${formatAsHour(new Date(date).getHours())}:${formatAsMinute(
    new Date(date).getMinutes()
  )}`;
};

export const getTimeInAMPM = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = formatAsHour(hours % 12 || 12); // Convert to 12-hour format
  const formattedMinutes = formatAsMinute(minutes);
  return `${formattedHours}:${formattedMinutes} ${ampm}`;
};

export const isToday = (isoDate: string): boolean => {
  const date = getDatePart(new Date(isoDate));
  const today = getDatePart(new Date());
  return date.getMilliseconds() === today.getMilliseconds();
};

export const joinTimeParts = (hour: string, minute: string): string =>
  `${formatAsHour(parseInt(hour))}:${formatAsMinute(parseInt(minute))}`;

export const isSameOrLater = (date1: Date, date2: Date): boolean => {
  const luxonDate1 = DateTime.fromISO(getLocalISO(date1));
  const luxonDate2 = DateTime.fromISO(getLocalISO(date2));

  return luxonDate1 >= luxonDate2;
};

export const isSameOrBefore = (date1: Date, date2: Date): boolean => {
  const luxonDate1 = DateTime.fromISO(getLocalISO(date1));
  const luxonDate2 = DateTime.fromISO(getLocalISO(date2));
  return luxonDate1 <= luxonDate2;
};

export const isLater = (date1: Date, date2: Date): boolean => {
  const luxonDate1 = DateTime.fromISO(getLocalISO(date1));
  const luxonDate2 = DateTime.fromISO(getLocalISO(date2));

  return luxonDate1 > luxonDate2;
};

export const isBefore = (date1: Date, date2: Date): boolean => {
  const luxonDate1 = DateTime.fromISO(getLocalISO(date1));
  const luxonDate2 = DateTime.fromISO(getLocalISO(date2));

  return luxonDate1 < luxonDate2;
};

export const sortDates = (dates: Date[]): Date[] =>
  (dates ?? []).sort((a, b) => {
    const dateA = DateTime.fromJSDate(a);
    const dateB = DateTime.fromJSDate(b);
    return dateA.toMillis() - dateB.toMillis();
  });

export const getTotalMonthsFromDates = (dates: Date[]): number => {
  if (!dates || dates.length === 0) return 0;

  const startDate = DateTime.fromJSDate(dates[0]);
  const endDate = DateTime.fromJSDate(dates[dates.length - 1]);

  return Math.floor(endDate.diff(startDate, "months").months + 1); // +1 to include the start month
};

export const getTimeDifference = (
  initialTime: Date,
  finalTime: Date = new Date(),
  differenceIn: "hours" | "minutes" | "seconds" | "days" = "minutes"
) => {
  const difference = DateTime.fromJSDate(initialTime)
    .diff(DateTime.fromJSDate(finalTime))
    .toMillis();

  let value = difference / 60000;

  switch (differenceIn) {
    case "hours":
      value = difference / 3600000;
      break;

    case "seconds":
      value = difference / 1000;
      break;

    // In minutes
    default:
      value = difference / 60000;
      break;
  }

  return Math.round(value);
};

export const getNearestTime = (
  allTimes: Date[],
  referenceDate: Date = new Date()
): Date => {
  if (allTimes?.length === 0) return referenceDate;

  let nearestTimeIndex = 0;
  allTimes.forEach((item, index) => {
    let timeDifference = getTimeDifference(item, referenceDate);

    let nearestTime = getTimeDifference(
      allTimes[nearestTimeIndex],
      referenceDate
    );

    timeDifference = timeDifference <= 0 ? 0 : timeDifference;
    nearestTime = nearestTime <= 0 ? 100000 : nearestTime;

    if (nearestTime > timeDifference) {
      nearestTimeIndex = index;
    }
  });

  return allTimes[nearestTimeIndex];
};

export const getDatesArray = (dates: string[]) =>
  (dates ?? []).map((date) => new Date(date));

export function getDayDifference(date1: Date, date2: Date): number {
  return Math.round(
    DateTime.fromJSDate(date2).diff(DateTime.fromJSDate(date1), "days").days
  );
}

export const utcToLocalClock = (utcString: string): Date => {
  const d = new Date(utcString); // UTC date
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
    d.getUTCMilliseconds()
  );
};
